import React, { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Badge {
  id: string;
  name: string;
  description: string;
  emoji: string;
  earned_at: string;
}

interface BadgeDisplayProps {
  badges: Badge[];
  maxDisplay?: number;
  size?: 'sm' | 'md' | 'lg';
}

const BadgeDisplay: React.FC<BadgeDisplayProps> = ({ 
  badges, 
  maxDisplay = 3,
  size = 'sm' 
}) => {
  const displayBadges = badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;
  
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  if (badges.length === 0) return null;

  return (
    <TooltipProvider>
      <div className="inline-flex items-center gap-0.5">
        {displayBadges.map((badge) => (
          <Tooltip key={badge.id}>
            <TooltipTrigger asChild>
              <span 
                className={`cursor-pointer hover:scale-110 transition-transform ${sizeClasses[size]}`}
                role="img" 
                aria-label={badge.name}
              >
                {badge.emoji}
              </span>
            </TooltipTrigger>
            <TooltipContent className="bg-background border shadow-lg p-2 max-w-[200px]">
              <div className="text-center">
                <div className="text-lg mb-1">{badge.emoji}</div>
                <div className="font-semibold text-sm">{badge.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{badge.description}</div>
              </div>
            </TooltipContent>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground cursor-pointer">
                +{remainingCount}
              </span>
            </TooltipTrigger>
            <TooltipContent className="bg-background border shadow-lg p-2">
              <div className="text-sm">
                {badges.slice(maxDisplay).map(b => (
                  <div key={b.id} className="flex items-center gap-1">
                    <span>{b.emoji}</span>
                    <span>{b.name}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export default BadgeDisplay;
